import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import api, { fmtMoney } from "../lib/api";
import { MagnifyingGlass, Buildings, ChartLineUp, ShieldCheck, ArrowRight, Sparkle } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1782392230060-be8a3984a58c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMGFmcmljYXxlbnwwfHx8fDE3ODQ4NDM1NzF8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/properties").then(({ data }) => {
      const s = [];
      data.forEach((p) => { if (p.media?.[0]) s.push({ id: p.id, img: p.media[0], title: p.title, city: p.city, country: p.country, price: p.sale_price || p.monthly_rent || p.event_price_per_day }); });
      setSlides(s.slice(0, 8));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [slides]);

  const chooseRole = (role) => {
    if (user) { navigate(role === "seller" ? "/dashboard" : "/catalog"); return; }
    sessionStorage.setItem("pending_role", role);
    navigate(`/auth?role=${role}`);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="overline text-xs text-orange-800 flex items-center gap-2">
              <Sparkle weight="fill" size={14} /> Immobilier intelligent · Afrique de l'Ouest
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 tracking-tight mt-4 leading-[1.05]">
              {settings?.hero_title || "Achetez, louez ou rentabilisez votre bien."}
            </h1>
            <p className="text-stone-600 text-base sm:text-lg mt-5 max-w-lg">
              {settings?.hero_subtitle || "Vente classique, location longue durée ou événementiel façon Airbnb — avec une IA qui estime vos loyers et analyse votre patrimoine."}
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-9">
              <button data-testid="role-buyer-btn" onClick={() => chooseRole("buyer")}
                className="group text-left bg-white border-2 border-stone-200 hover:border-orange-800 rounded-2xl p-6 transition-colors">
                <MagnifyingGlass size={30} weight="duotone" className="text-orange-800" />
                <div className="font-display text-lg font-bold text-stone-900 mt-3">Je suis Acheteur / Locataire</div>
                <div className="text-sm text-stone-500 mt-1 flex items-center gap-1">Explorer les biens <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></div>
              </button>
              <button data-testid="role-seller-btn" onClick={() => chooseRole("seller")}
                className="group text-left bg-slate-900 hover:bg-slate-800 rounded-2xl p-6 transition-colors">
                <Buildings size={30} weight="duotone" className="text-orange-400" />
                <div className="font-display text-lg font-bold text-white mt-3">Je suis Vendeur / Propriétaire</div>
                <div className="text-sm text-stone-300 mt-1 flex items-center gap-1">Publier & analyser <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></div>
              </button>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="rounded-3xl overflow-hidden border border-stone-200 shadow-2xl">
              <img src={HERO} alt="Villa de luxe" className="w-full h-[380px] lg:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-2 sm:left-6 bg-white rounded-2xl border border-stone-200 shadow-xl p-4 flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-orange-100 grid place-items-center"><ChartLineUp weight="fill" size={22} className="text-orange-800" /></span>
              <div>
                <div className="text-xs text-stone-500">Rendement estimé IA</div>
                <div className="font-display text-xl font-bold text-stone-900">+12,4%<span className="text-sm text-stone-400 font-sans"> /an</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAROUSEL — biens du site */}
      {slides.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-4" data-testid="home-carousel">
          <div className="flex items-end justify-between mb-4">
            <div>
              <span className="overline text-xs text-orange-800">À la une</span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 mt-1">Biens disponibles</h2>
            </div>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-orange-800" : "w-2 bg-stone-300"}`} />
              ))}
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-stone-200 h-[300px] sm:h-[420px] bg-stone-900">
            {slides.map((s, i) => (
              <button key={s.id} onClick={() => navigate(`/property/${s.id}`)}
                className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/70 to-transparent text-left">
                  <div className="text-white/80 text-sm text-shadow-soft">{s.city}{s.country ? `, ${s.country}` : ""}</div>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-white text-shadow-soft">{s.title}</div>
                  <div className="text-orange-300 font-bold text-shadow-soft mt-1">{fmtMoney(s.price)}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-3 gap-6">
        {[
          { i: <ChartLineUp size={26} weight="duotone" />, t: "Analyse patrimoniale IA", d: "Estimation du loyer idéal, valeur foncière et perspectives de plus-value sur 5 ans." },
          { i: <Buildings size={26} weight="duotone" />, t: "3 modes d'exploitation", d: "Vente classique, location longue durée ou événementiel (soirées, réceptions, concerts)." },
          { i: <ShieldCheck size={26} weight="duotone" />, t: "Paiements sécurisés", d: "Visa, Mastercard, PayPal ainsi que Orange Money & Wave pour vos réservations." },
        ].map((f, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-2xl p-7 hover:border-orange-300 transition-colors fade-up" style={{ animationDelay: `${i * 80}ms` }}>
            <span className="w-12 h-12 rounded-xl bg-orange-50 text-orange-800 grid place-items-center">{f.i}</span>
            <h3 className="font-display text-xl font-bold text-stone-900 mt-4">{f.t}</h3>
            <p className="text-stone-600 text-sm mt-2 leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-stone-200 py-8 text-center text-stone-400 text-sm">
        © 2026 Lomar Properties — Plateforme immobilière intelligente.
      </footer>
    </div>
  );
}
